import { yupResolver } from "@hookform/resolvers/yup";
import DeleteIcon from "@mui/icons-material/Delete";
import { useEffect } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { MdmFormProps } from "../../../Forms/MdmFormProps";
import { INFORMATION } from "../../../Layout/Tabs/MdmTabPanelConstants";
import PageHeader from "../../../Layout/Header/PageHeader";
import PageHeaderButton from "../../../Layout/Header/PageHeaderButton";
import PageHeaderIconButton from "../../../Layout/Header/PageHeaderIconButton";
import { MdmTabs } from "../../../Layout/Tabs/MdmTabs";
import { defaultRateAgreement, RateAgreement } from "../RateAgreement";
import { RateAgreementBidPackForm } from "./BidPack/RateAgreementBidPackForm";
import { RateAgreementInformationForm } from "./Information/RateAgreementInformationForm";
import { rateAgreementSchema } from "./rateAgreementSchema";
import { confirm } from "../../../Layout/Dialog/ConfirmDialog";
import { isFormSubmitForbidden } from "../../../Forms/isFormSubmitForbidden";
import { resetFormStateAndKeepFormValues } from "../../../Forms/resetFormStateAndKeepFormValues";
import { useIsUserInRoles } from "../../../Auth/useIsUserInRoles";
import { AuthorizationPolicies } from "../../../Auth/AuthorizationPolicy";

type Props = {
  title: string;
  rateAgreement: RateAgreement | undefined;
  onSave: (rateAgreement: RateAgreement) => void;
  onDelete?: (rateAgreementId: number | null | undefined) => void;
  saveButtonText?: string;
};

export const RateAgreementFormTabs = ({
  title,
  rateAgreement,
  onSave,
  disabledFields,
  onDelete,
  saveButtonText = "SAVE",
}: Props & MdmFormProps) => {
  const isUserInAdminNetworkRole = useIsUserInRoles(AuthorizationPolicies.RequireAdminNetworkRoles);

  const methods = useForm<RateAgreement>({
    defaultValues: defaultRateAgreement,
    resolver: yupResolver(rateAgreementSchema),
    mode: "all",
    disabled: !isUserInAdminNetworkRole,
  });

  useEffect(() => {
    methods.reset(rateAgreement);

    return () => methods.reset(defaultRateAgreement);
  }, [rateAgreement, methods, methods.reset]);

  const onDeleteWithConfirmation = async () => {
    const isConfirmed = await confirm(
      `Do you really want to delete Rate Agreement with Id ${rateAgreement?.Id}?`
    );
    if (!isConfirmed || !onDelete) {
      return;
    }
    await onDelete(rateAgreement?.Id);
  };

  return (
    <>
      <PageHeader headerText={title}>
        {onDelete !== undefined && (
          <PageHeaderIconButton
            onClick={() => onDeleteWithConfirmation()}
            color="secondary"
            disabled={!isUserInAdminNetworkRole}
          >
            <DeleteIcon />
          </PageHeaderIconButton>
        )}
        <PageHeaderButton
          onClick={methods.handleSubmit((values: RateAgreement) => {
            onSave(values);
            resetFormStateAndKeepFormValues(methods);
          })}
          disabled={isFormSubmitForbidden(methods.formState)}
        >
          {saveButtonText}
        </PageHeaderButton>
      </PageHeader>

      <FormProvider {...methods}>
        <MdmTabs
          tabs={[
            {
              index: 0,
              label: INFORMATION,
              content: <RateAgreementInformationForm disabledFields={disabledFields} />,
            },
            {
              index: 1,
              label: "BID PACK",
              content: <RateAgreementBidPackForm rateAgreementId={rateAgreement?.Id ?? 0} />,
              disabled: !rateAgreement,
            },
          ]}
        />
      </FormProvider>
    </>
  );
};
